import React, { useState, useEffect, useRef } from 'react';
import styles from './CompactTokenPairMetrics.module.sass';

const CompactTokenPairMetrics = ({ pairData, selectedChain }) => {
  const [currentData, setCurrentData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('24h');
  const [activeExchange, setActiveExchange] = useState('AGGREGATE');
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const timePeriods = ['5m', '1h', '4h', '24h'];

  // Mouse event handlers for scrolling
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  // Get unique exchanges from pair data
  const getExchanges = () => {
    if (!Array.isArray(pairData)) return ['AGGREGATE'];
    
    const exchanges = new Set();
    
    // First, find and add AGGREGATE if it exists
    const aggregateData = pairData.find(pair => pair.exchange === 'AGGREGATE');
    if (aggregateData) {
      exchanges.add('AGGREGATE');
    }
    
    // Then add other exchanges
    pairData.forEach(pair => {
      if (pair?.exchange && pair.exchange !== 'AGGREGATE') {
        exchanges.add(pair.exchange.toLowerCase());
      }
    });
    
    return Array.from(exchanges);
  };

  useEffect(() => {
    if (Array.isArray(pairData)) {
      // Find data for active exchange
      const exchangeData = activeExchange === 'AGGREGATE'
        ? pairData.find(pair => pair.exchange === 'AGGREGATE')
        : pairData.find(pair => pair?.exchange?.toLowerCase() === activeExchange);

      if (exchangeData && exchangeData.totalVolume) {
        setCurrentData(exchangeData);
      }
    }
  }, [pairData, activeExchange]);

  const formatNumber = (num) => {
    if (!num) return '$0.00';
    if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  if (!currentData) {
    return <div className={styles.container}>Loading metrics...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.glowTop}></div>
      <div className={styles.glowBottom}></div>
   
      <div className={styles.content}>
        {/* Exchange Selector */}
        <div className={styles.exchangeSelector}>
          <div
            ref={scrollRef}
            className={styles.exchangeList}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              overflowX: 'auto',
              display: 'flex',
              gap: '8px',
              paddingBottom: '8px'
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseUp}
          >
            {getExchanges().map(exchange => (
              <button
                key={exchange}
                onClick={() => setActiveExchange(exchange === 'aggregate' ? 'AGGREGATE' : exchange)}
                className={`${styles.exchangeButton} ${activeExchange === (exchange === 'aggregate' ? 'AGGREGATE' : exchange) ? styles.activeExchange : ''}`}
              >
                {exchange === 'AGGREGATE' ? (
                  'Aggregate'
                ) : (
                  <div className={styles.exchangeOption}>
                    <img
                      src={pairData.find(p => p.exchange?.toLowerCase() === exchange)?.exchangeLogo}
                      alt={exchange}
                      className={styles.exchangeIcon}
                    />
                    {exchange}
                  </div>
                )}
              </button>
            ))}
          </div>
          <div className={styles.periodButtons}>
            {timePeriods.map(period => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`${styles.periodButton} ${selectedPeriod === period ? styles.activePeriod : ''}`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        {/* Metrics */}
        <div className={styles.metrics}>
          <div className={styles.metricRow}>
            {/* Volume Metric */}
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Volume {selectedPeriod}</span>
              <span className={styles.metricValue}>
                {formatNumber(currentData.totalVolume?.[selectedPeriod] ?? 0)}
              </span>
              <div className={styles.subMetrics}>
                <span className={styles.buyMetric}>
                  Buy: {formatNumber(currentData.buyVolume?.[selectedPeriod] ?? 0)}
                </span>
                <span className={styles.sellMetric}>
                  Sell: {formatNumber(currentData.sellVolume?.[selectedPeriod] ?? 0)}
                </span>
              </div>
            </div>

            {/* Liquidity Metric */}
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Liquidity</span>
              <span className={styles.metricValue}>
                {formatNumber(currentData.totalLiquidityUsd ?? 0)}
              </span>
              <span 
                className={
                  (currentData.liquidityPercentChange?.[selectedPeriod] ?? 0) >= 0 
                  ? styles.positive 
                  : styles.negative
                }
              >
                {(currentData.liquidityPercentChange?.[selectedPeriod] ?? 0) > 0 ? '+' : ''}
                {(currentData.liquidityPercentChange?.[selectedPeriod] ?? 0).toFixed(2)}%
              </span>
            </div>
          </div>

          {/* Traders Metric */}
          <div className={styles.metricRow}>
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Traders {selectedPeriod}</span>
              <span className={styles.metricValue}>
                {(currentData.buyers?.[selectedPeriod] ?? 0) + (currentData.sellers?.[selectedPeriod] ?? 0)}
              </span>
              <div className={styles.subMetrics}>
                <span className={styles.buyMetric}>
                  Buy: {currentData.buyers?.[selectedPeriod] ?? 0}
                </span>
                <span className={styles.sellMetric}>
                  Sell: {currentData.sellers?.[selectedPeriod] ?? 0}
                </span>
              </div>
            </div>

            {/* Transactions Metric */}
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Transactions {selectedPeriod}</span>
              <span className={styles.metricValue}>
                {(currentData.buys?.[selectedPeriod] ?? 0) + (currentData.sells?.[selectedPeriod] ?? 0)}
              </span>
              <div className={styles.subMetrics}>
                <span className={styles.buyMetric}>
                  Buy: {currentData.buys?.[selectedPeriod] ?? 0}
                </span>
                <span className={styles.sellMetric}>
                  Sell: {currentData.sells?.[selectedPeriod] ?? 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompactTokenPairMetrics;